import { Client as AWClient, Account, AppwriteException, ID } from 'appwrite';
import type { Models } from 'appwrite';
import { PROJECT_ID, PROJECT_ENDPOINT } from './appwriteApi';
import { LicenseService } from './licenseService';

export type AuthProxyFunctionType =
    (<TResult = unknown, TArgs extends unknown[] = []>(funcName: string, ...args: TArgs) => Promise<TResult>) | null;

export type ValidationResult = {
    success: boolean;
    userId?: string;
    sessionId?: string;
    expire?: string;
};

export type LoginCodeResponse = {
    userId: string;
};

export class AuthService {
    private readonly client: AWClient;
    private readonly account: Account;

    constructor() {
        this.client = new AWClient();

        this.client.setEndpoint(PROJECT_ENDPOINT).setProject(PROJECT_ID);

        this.account = new Account(this.client);
    }

    async sendLoginCode(email: string): Promise<LoginCodeResponse> {
        const normalizedEmail: string = email.trim().toLowerCase();
        const token: Models.Token = await this.account.createEmailToken(ID.unique(), normalizedEmail);
        return { userId: token.userId };
    }

    private async clearActiveSession(): Promise<void> {
        try {
            await this.account.deleteSession('current');
            return;
        } catch {
            // Ignore and try to clear all sessions as a fallback.
        }

        try {
            await this.account.deleteSessions();
        } catch {
            // Ignore if there was no active session to delete.
        }
    }

    private isActiveSessionError(error: unknown): error is AppwriteException {
        if (!(error instanceof AppwriteException)) return false;
        if (error.code !== 401) return false;
        return /Creation of a session is prohibited when a session is active/i.test(error.message);
    }

    async validateLoginCode(userId: string, secret: string): Promise<ValidationResult> {
        const createSession = async (): Promise<ValidationResult> => {
            await this.account.createSession(userId, secret);
            const session: Models.Session = await this.account.getSession('current');
            return { success: true, userId: session.userId, sessionId: session.$id, expire: session.expire };
        };

        try {
            return await createSession();
        } catch (error: unknown) {
            if (this.isActiveSessionError(error)) {
                await this.clearActiveSession();
                try {
                    return await createSession();
                } catch (retryError: unknown) {
                    const retryMessage: string = retryError instanceof Error ? retryError.message : String(retryError);
                    console.error('Verification failed after clearing session:', retryMessage);
                    return { success: false };
                }
            }

            const message: string = error instanceof Error ? error.message : String(error);
            console.error('Verification failed:', message);
            return { success: false };
        }
    }

    async logout(): Promise<void> {
        try {
            // Delete all sessions to ensure clean logout
            await this.account.deleteSessions();
            LicenseService.clear();
        } catch (error: unknown) {
            const message: string = error instanceof Error ? error.message : String(error);
            console.error('Logout failed:', message);
        }
    }
}
